<template>
    <v-form>
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>
            <v-card-text>
                <v-container grid-list-md>
                    <v-layout wrap>
                        <v-flex xs6>
                            <v-text-field
                                v-model="value.shortName"
                                label="What is the display name?"
                                placeholder="e.g. PM 250"
                                v-validate="'required|unique'"
                                :error-messages="errors.first('short name')"
                                :disabled="isSpecialServiceRequirement(this.value.id)"
                                name="short name"
                                maxlength="120"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                            <v-text-field
                                v-model="value.longName"
                                label="What is the description?"
                                placeholder="e.g. Preventative Maintenance 250 hrs"
                                v-validate="'required'"
                                :error-messages="errors.first('long name')"
                                name="long name"
                                maxlength="150"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs6>
                            <v-text-field
                                v-model="value.interval"
                                label="This occurs every"
                                v-validate="'required|min_value:1'"
                                :error-messages="errors.collect('first interval')"
                                name="first interval"
                                type="number"
                                min="1"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs3>
                            <v-select
                                label="hrs / km / months / days"
                                item-value="key"
                                item-text="value"
                                :items="intervalTypes"
                                v-model="value.intervalType"
                                v-validate="'required'"
                                :error-messages="errors.collect('first interval type')"
                                name="first interval type"
                                required
                                :disabled="editing"
                            ></v-select>
                        </v-flex>
                        <v-flex xs3 class="tolerance-field">
                            <v-text-field
                                v-model="value.tolerance"
                                label="within +/-"
                                placeholder="tolerance"
                                v-validate="'required|min_value:1'"
                                :error-messages="errors.collect('first tolerance')"
                                name="first tolerance"
                                type="number"
                                min="1"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                            <v-checkbox
                                v-model="value.hasSecondaryRequirement"
                                :disabled="alreadyConfiguredWithSecondary"
                            >
                                <template v-slot:label>OR</template>
                            </v-checkbox>
                        </v-flex>
                        <v-flex v-if="value.hasSecondaryRequirement" xs6>
                            <v-text-field
                                v-model="value.interval2"
                                label="This occurs every"
                                v-validate="'required|min_value:1'"
                                :error-messages="errors.collect('second interval')"
                                name="second interval"
                                type="number"
                                min="1"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex v-if="value.hasSecondaryRequirement" xs3>
                            <v-select
                                label="hrs / km / months / days"
                                item-value="key"
                                item-text="value"
                                :items="intervalTypes"
                                v-model="value.intervalType2"
                                v-validate="'required'"
                                :error-messages="errors.collect('second interval type')"
                                name="second interval type"
                                required
                                :disabled="this.alreadyConfiguredWithSecondary"
                            ></v-select>
                        </v-flex>
                        <v-flex v-if="value.hasSecondaryRequirement" xs3 class="tolerance-field">
                            <v-text-field
                                v-model="value.tolerance2"
                                label="within +/-"
                                placeholder="tolerance"
                                v-validate="'required|min_value:1'"
                                :error-messages="errors.collect('second tolerance')"
                                name="second tolerance"
                                type="number"
                                min="1"
                                required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs4>
                          <v-checkbox v-model="endsAfter" @change="endsAfterChange" label="Ends after" />
                        </v-flex>
                        <v-flex xs8>
                            <v-text-field
                                v-model="value.recurrence"
                                label="Occurrences"
                                v-validate="endsAfter ? 'required|min_value:1|max_value:40000' : ''"
                                :error-messages="errors.collect('occurrences')"
                                name="occurrences"
                                type="number"
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                            <v-select
                                label="What type of service?"
                                item-value="key"
                                item-text="value"
                                :items="serviceTypes"
                                v-model="value.serviceType"
                                v-validate="'required'"
                                :error-messages="errors.collect('service type')"
                                name="service type"
                                required
                            ></v-select>
                        </v-flex>
                        <v-flex xs12>
                            <v-select
                                label="Is documentation required?"
                                item-value="key"
                                item-text="value"
                                :items="documentRequirementTypes"
                                v-model="value.requiresDocumentation"
                                v-validate="'required'"
                                :error-messages="errors.collect('requires documentation')"
                                name="requires documentation"
                                required
                            ></v-select>
                        </v-flex>
                        <v-flex xs12 v-if="requiresDocumentationEnabled">
                            <v-menu
                                v-model="documentationDateMenu"
                                max-width="290px"
                                :close-on-content-click="false"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        :value="formattedDocumentationRequiredFromDate"
                                        label="Documentation required from date"
                                        readonly
                                        v-on="on"
                                        v-validate="'required'"
                                        :error-messages="errors.first('documentation required from date')"
                                        name="documentation required from date"
                                        required
                                        append-icon="event"
                                        @click:append="documentationDateMenu = true"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="value.documentationRequiredFromDate"
                                    @change="documentationDateMenu = false"
                                    no-title
                                ></v-date-picker>
                            </v-menu>
                        </v-flex>
                        <v-flex xs12 v-if="requiresDocumentationEnabled">
                            <v-select
                                label="What type of document?"
                                item-value="key"
                                item-text="value"
                                :items="documentTypes"
                                v-model="value.documentType"
                                :error-messages="errors.collect('document type')"
                                name="document type"
                                required
                            ></v-select>
                        </v-flex>                    
                    </v-layout>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <slot name="actions"></slot>
                <v-btn
                    color="primary"
                    :disabled="disableSubmit"
                    @click="submit"
                >{{ !this.editing ? 'Save Changes' : 'Update' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>
  import {TaskType, IntervalType, DocumentType, DocumentRequirementType} from '../../models/enums';
  import ServiceRequirements from '../ServiceRequirements/ServiceRequirementsClient.vue';
  import {Validator} from 'vee-validate';
  import FormValidation from '../FormValidation.vue';
  import moment from 'moment';
  import {DateFormat} from '../../plugins/date-filter';

  export default {
    name: 'ServiceRequirementForm',
    props: {
      value: {
        required: true
      }
    },
    mixins: [FormValidation, ServiceRequirements],
    computed: {
      formTitle() {
        return !this.editing ? 'Add Service Requirement' : 'Edit Service Requirement';
      },
      editing() {
        return !!this.value.id;
      },
      requiresDocumentationEnabled() {
        return this.value.requiresDocumentation === DocumentRequirementType.Required ||
               this.value.requiresDocumentation === DocumentRequirementType.Optional;
      },
      formattedDocumentationRequiredFromDate() {
        return this.value.documentationRequiredFromDate ? moment(this.value.documentationRequiredFromDate).format(DateFormat.DefaultDisplay) : '';
      }
    },
    methods: {
      submitForm() {
        this.submitted = true;
        this.addOrUpdate();
      },
      addOrUpdate() {
        if (!this.editing) {
          this.$emit('add', {id: this.id, model: this.value});
        } else {
          this.$emit('edit', {id: this.value.id, model: this.value});
        }
        this.endsAfter = false;
      },
      async checkNameExists(shortName) {
        try {
          const response = await this.$api.get(`service-requirements/${shortName}`);
          const requirements = response.data;
          if (requirements.find(x => x == this.value.id)) {
            return {
              valid: true
            };
          }
        } catch (error) {
          if (error.response.status === 404) {
            return {
              valid: true
            };
          }
        }

        return {
          valid: false,
          data: {
            message: `${shortName} is already taken.`
          }
        };
      },
      endsAfterChange() {
        if (!this.endsAfter) {
          this.value.recurrence = null;
        }
      }
    },
    data() {
      return {
        id: '',
        id2: '',
        submitted: false,
        alreadyConfiguredWithSecondary: false,
        intervalTypes: [
          {
            key: IntervalType.Hours,
            value: 'hrs'
          },
          {
            key: IntervalType.Kilometres,
            value: 'km'
          },
          {
            key: IntervalType.Months,
            value: 'months'
          },
          {
            key: IntervalType.Days,
            value: 'days'
          }
        ],
        serviceTypes: [
          {
            key: TaskType.PreventativeMaintenance,
            value: 'Preventative Maintenance'
          },
          {
            key: TaskType.MajorService,
            value: 'Major Service'
          },
          {
            key: TaskType.Brakes,
            value: 'Brake Check'
          },
          {
            key: TaskType.ContainerInspection,
            value: 'Container Inspection'
          }
        ],
        documentTypes: [
          {
            key: DocumentType.ServiceSheet,
            value: 'Service Sheet'
          },
          {
            key: DocumentType.NMICertificate,
            value: 'NMI Certificate'
          },
          {
            key: DocumentType.Other,
            value: 'Other'
          }
        ],
        documentRequirementTypes: [
          {
            key: DocumentRequirementType.NotApplicable,
            value: 'No'
          },
          {
            key: DocumentRequirementType.Required,
            value: 'Yes'
          },
          {
            key: DocumentRequirementType.Optional,
            value: 'Optional'
          }
        ],
        endsAfter: false,
        documentationDateMenu: false
      };
    },
    watch: {
      value(val) {
        if (val.id) {
          this.id = val.id;
        } else {
          this.id = this.$uuid.v1();
        }

        if (!val.id2) {
          this.value.id2 = this.$uuid.v1();
        }

        if (!this.value.loaded) {
          this.value.loaded = true;
          this.alreadyConfiguredWithSecondary = val.hasSecondaryRequirement;
        }

        this.submitted = false;
        this.endsAfter = !!val.recurrence;
      },
      'value.requiresDocumentation'(newVal) {
        if (newVal === DocumentRequirementType.NotApplicable) {
          this.value.documentationRequiredFromDate = null;
          this.value.documentType = null;
        }
      }
    },
    created() {
      const isUnique = async value => await this.checkNameExists(value);
      this.endsAfter = !!this.value.recurrence;

      Validator.extend('unique', {
        validate: isUnique,
        getMessage: (field, params, data) => data.message
      });
    }
  };
</script>
